import { z } from "zod";
import api from "../api";
import { getHashedPassword } from "../utils/generalUtilities";
import { passwordValidator } from "../utils/password-validator";
import { clearUserSession } from "../utils/sessionCleanup";

type UpdatePasswordParams = {
  userAccountID: string;
  currentPassword: string;
  newPassword: string;
};

export const userLogin = async ({
  username,
  password,
  recaptchaToken,
}: {
  username: string;
  password: string;
  recaptchaToken?: string;
}) => {
  return await api.post(`/Employee/Login`, {
    userName: username,
    password: await getHashedPassword(password),
    recaptchaToken,
  });
};

export const createPasswordSchema = z.object({
  password: z
    .string({ required_error: "Password is required." })
    .trim()
    .superRefine((password, ctx) => {
      const passwordTestResult = passwordValidator(password);
      Object.keys(passwordTestResult).map((rule) => {
        const ruleCtx =
          passwordTestResult[rule as keyof typeof passwordTestResult];
        if (!ruleCtx.validator) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: ruleCtx?.message,
            path: [rule],
          });
        }
      });
    }),
});

export const resetPasswordSchema = z
  .object({
    password: z
      .string({ required_error: "Password is required." })
      .min(1, "Password is required.")
      .trim()
      .superRefine((password, ctx) => {
        const passwordTestResult = passwordValidator(password);
        Object.keys(passwordTestResult).map((rule) => {
          const ruleCtx =
            passwordTestResult[rule as keyof typeof passwordTestResult];
          if (!ruleCtx.validator) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: ruleCtx?.message,
              path: [rule],
            });
          }
        });
      }),
    confirmPassword: z
      .string({ required_error: "Confirm password is required." })
      .min(1, "Confirm password is required."),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

export type createPasswordInput = z.infer<typeof createPasswordSchema>;

export type resetPasswordInput = z.infer<typeof resetPasswordSchema>;

export const updatePassword = async ({
  userAccountID,
  currentPassword,
  newPassword,
}: UpdatePasswordParams): Promise<void> => {
  const hashedCurrentPassword = await getHashedPassword(currentPassword);
  const hashedNewPassword = await getHashedPassword(newPassword);

  return await api.post("/UserAccount/SetPassword", {
    userAccountID: userAccountID,
    currentPassword: hashedCurrentPassword,
    password: hashedNewPassword,
    modifyingUserAccountID: userAccountID,
    sourceProcess: "WebApplication",
    changeReason: "Update Password",
  });
};

/**
 * Notifies the backend that the user is logging out. Tokens are read by the
 * server from HttpOnly cookies — no body payload needed.
 */
export const logoutUserApi = async () => {
  return await api.post("/UserAccount/Logout");
};

/**
 * User-initiated logout. Best-effort: always tears down the local session and
 * redirects even if the backend call fails or times out, so the user is never
 * trapped in a logged-in state.
 */
export const logoutUser = async () => {
  try {
    await logoutUserApi();
  } catch {
    // best-effort — log out locally regardless of the backend response
  }
  await clearUserSession();
  return false;
};

export const generateResetPasswordCode = async (userName: string) => {
  return await api.post("/UserAccount/ResetPassword", {
    userName,
    sourceProcess: "WebApplication",
  });
};

export const verifyVerificationCode = async (code: string) => {
  return await api.post("/UserAccount/ResetPassword/Check", {
    passwordResetVerificationCode: code,
    sourceProcess: "WebApplication",
  });
};

export const setNewPasswordAPI = async (
  password: string,
  userID: string,
  code: string,
) => {
  const hashedPassword = await getHashedPassword(password);
  const url = `/UserAccount/ResetPassword/${(code ?? "").trim()}`;
  return await api.put(url, {
    UserAccountID: userID,
    Password: hashedPassword,
    ModifyingUserAccountID: userID,
    SourceProcess: "WebApplication",
    ChangeReason: "Reset password",
  });
};

export const getAccountNumber = async (userAccountID: string) => {
  return await api.get(`/Account/details/${userAccountID}`);
};

export const updateUserAccount = async (
  id: number | string,
  firstName: string,
  middleName: string,
  lastName: string,
  dateOfBirth: string,
  telephoneNumber: string | null,
  emailAddress: string,
) => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const res: any = await api.put("/UserAccount/Update/Profile", {
      userAccountID: id,
      firstName,
      lastName,
      dateOfBirth,
      telephoneNumber,
      emailAddress,
      middleName,
      modifyingUserAccountID: id,
      sourceProcess: "WebApplication",
      changeReason: "Update UserAccount",
    });

    return res;
  } catch {
    //    toast.error("Failed to update account information. Please try again.");
  }
};

export const getLegalTerms = async (docType: string) => {
  return await api.get(`/TermsAndConditions/version/${docType}`);
};

export const acceptLegalTerms = async (docType: string, version: number) => {
  return await api.post(`/TermsAndConditions`, {
    docType,
    version,
  });
};

export const getIframeToken = async (): Promise<{
  token: string;
  expiresInSeconds: number;
}> => {
  const { data } = await api.get<{ token: string; expiresInSeconds: number }>(
    "/UserAccount/IframeToken",
  );
  return data;
};
